import { useCallback } from "react";

import { DEFAULT_SLIDER_CONFIG, type HumanInputComponentType } from "../constants";

import { useBlock } from "@/components/PowerBlock/Block/block-store";
import {
  getHumanConfigurationForComponent,
  HumanUIComponentType,
} from "@/routes/smart-table/utils/humanInput";
import { HumanConfiguration } from "@/types/evaluate";

interface HumanInputConfigurationHook {
  configuration: HumanConfiguration | undefined;
  dataType: "number" | "string" | undefined;
  uiComponent: HumanInputComponentType | undefined;
  sliderConfig: { min: number; max: number } | undefined;
  ratingConfig: { max: number } | undefined;
  choiceConfig: { options: string[] } | undefined;
  handleDataTypeChange: (dataType: "number" | "string") => void;
  handleUiComponentChange: (component: string) => void;
  handleSliderConfigChange: (key: "min" | "max", value: string) => void;
  handleRatingConfigChange: (value: string) => void;
  handleChoiceOptionChange: (index: number, value: string) => void;
  handleAddChoiceOption: () => void;
  handleRemoveChoiceOption: (index: number) => void;
  handleSmartTableComponentChange: (component: Exclude<HumanInputComponentType, "text">) => void;
  ensureSmartTableConfiguration: () => void;
}

const useHumanInputConfiguration = (): HumanInputConfigurationHook => {
  const blockStore = useBlock<HumanConfiguration>();

  const handleDataTypeChange = useCallback(
    (dataType: "number" | "string") => {
      blockStore.setConfiguration({
        data_type: dataType,
        ui_element: { component: "text" },
      });
    },
    [blockStore],
  );

  const handleUiComponentChange = useCallback(
    (component: string) => {
      if (component === "slider" && blockStore.configuration?.data_type === "number") {
        blockStore.setConfiguration({
          data_type: "number",
          ui_element: {
            component: "slider",
            min: DEFAULT_SLIDER_CONFIG.min,
            max: DEFAULT_SLIDER_CONFIG.max,
          },
        });
      } else {
        blockStore.setConfiguration({
          ...blockStore.configuration!,
          ui_element: { component: "text" },
        });
      }
    },
    [blockStore],
  );

  const handleSmartTableComponentChange = useCallback(
    (component: Exclude<HumanInputComponentType, "text">) => {
      blockStore.setConfiguration(
        getHumanConfigurationForComponent(component, blockStore.configuration),
      );
    },
    [blockStore],
  );

  const ensureSmartTableConfiguration = useCallback(() => {
    const component = blockStore.configuration?.ui_element?.component;
    if (
      component === HumanUIComponentType.SLIDER ||
      component === HumanUIComponentType.RATING ||
      component === HumanUIComponentType.CHOICE
    ) {
      return;
    }

    blockStore.setConfiguration(
      getHumanConfigurationForComponent(HumanUIComponentType.SLIDER, blockStore.configuration),
    );
  }, [blockStore]);

  const updateUIElement = useCallback(
    (
      component: Exclude<HumanInputComponentType, "text">,
      updates: { min?: number; max?: number; options?: string[] },
    ) => {
      const { configuration } = blockStore;
      if (configuration?.ui_element.component !== component) {
        return;
      }

      blockStore.setConfiguration({
        ...configuration,
        ui_element: { ...configuration.ui_element, ...updates },
      } as HumanConfiguration);
    },
    [blockStore],
  );

  const handleSliderConfigChange = useCallback(
    (key: "min" | "max", value: string) =>
      updateUIElement(HumanUIComponentType.SLIDER, { [key]: Number(value) }),
    [updateUIElement],
  );

  const handleRatingConfigChange = useCallback(
    (value: string) => updateUIElement(HumanUIComponentType.RATING, { max: Number(value) }),
    [updateUIElement],
  );

  const updateChoiceOptions = useCallback(
    (updater: (options: string[]) => string[]) => {
      const uiElement = blockStore.configuration?.ui_element;
      if (uiElement?.component === HumanUIComponentType.CHOICE) {
        updateUIElement(HumanUIComponentType.CHOICE, {
          options: updater(uiElement.options),
        });
      }
    },
    [blockStore.configuration?.ui_element, updateUIElement],
  );

  const handleChoiceOptionChange = useCallback(
    (index: number, value: string) =>
      updateChoiceOptions(options => options.map((option, i) => (i === index ? value : option))),
    [updateChoiceOptions],
  );

  const handleAddChoiceOption = useCallback(
    () => updateChoiceOptions(options => [...options, `Option ${options.length + 1}`]),
    [updateChoiceOptions],
  );

  const handleRemoveChoiceOption = useCallback(
    (index: number) =>
      updateChoiceOptions(options =>
        options.length > 2 ? options.filter((_, i) => i !== index) : options,
      ),
    [updateChoiceOptions],
  );

  const dataType = blockStore.configuration?.data_type;
  const uiComponent = blockStore.configuration?.ui_element?.component;
  const sliderConfig = uiComponent === "slider" ? blockStore.configuration?.ui_element : undefined;
  const ratingConfig = uiComponent === "rating" ? blockStore.configuration?.ui_element : undefined;
  const choiceConfig = uiComponent === "choice" ? blockStore.configuration?.ui_element : undefined;

  return {
    configuration: blockStore.configuration,
    dataType,
    uiComponent,
    sliderConfig,
    ratingConfig,
    choiceConfig,
    handleDataTypeChange,
    handleUiComponentChange,
    handleSliderConfigChange,
    handleRatingConfigChange,
    handleChoiceOptionChange,
    handleAddChoiceOption,
    handleRemoveChoiceOption,
    handleSmartTableComponentChange,
    ensureSmartTableConfiguration,
  };
};

export default useHumanInputConfiguration;
